import { Lock, X } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import { DimeInput } from '../DimeInput.js';
import { DimeTooltip } from '../DimeTooltip.js';
import { cn } from '../lib/utils.js';
import type { DimeEditableCellProps } from './DimeEditableCell.types.js';
import { resolveEditableCellDisplay } from './editable-cell-state.js';
import { useEditableCell } from './use-editable-cell.js';

export type { DimeEditableCellProps, EditableCellState } from './DimeEditableCell.types.js';

/**
 * Click-to-edit cell for `DimeTreeTable`/`DimeDataTable` column defs — a `<button>` in display
 * mode, an `<input>` in edit mode, both labelled by `ariaLabel`. Built for the price sheet
 * (NUDOOL-27 §9/R5), but currency-agnostic: `format`/`parse` decide what `TValue` means.
 */
export function DimeEditableCell<TValue = number>(props: Readonly<DimeEditableCellProps<TValue>>) {
  const {
    value,
    onCommit,
    format,
    parse,
    state,
    locked = false,
    disabled = false,
    disabledTooltip,
    ariaLabel,
    isSaving = false,
    error,
    required = false,
    inheritedFrom,
    emptyText,
    sanitizeInput,
    onDraftChange,
    onCancel,
    commitUnchanged,
    className,
  } = props;

  const {
    mode,
    draftText,
    setDraftText,
    enterEditMode,
    handleInputKeyDown,
    handleInputBlur,
    handleClear,
    inputRef,
    displayRef,
    handleDisplayFocus,
  } = useEditableCell<TValue>({
    value,
    onCommit,
    format,
    parse,
    isSaving,
    disabled,
    onCancel,
    commitUnchanged,
  });

  const errorId = React.useId();
  // aria-label always wins over a button's content in the accessible-name computation, so folding
  // "(locked)" into the label itself is what actually announces it — the icon below is decorative.
  const composedAriaLabel = locked ? `${ariaLabel} (locked)` : ariaLabel;

  if (mode === 'editing') {
    return (
      <>
        <div className="relative">
          <DimeInput
            showClear={false}
            ref={inputRef}
            type="text"
            inputMode="decimal"
            aria-label={composedAriaLabel}
            aria-invalid={Boolean(error)}
            aria-required={required || undefined}
            aria-describedby={error ? errorId : undefined}
            disabled={isSaving}
            value={draftText}
            onChange={(evt) => {
              const text = sanitizeInput ? sanitizeInput(evt.target.value) : evt.target.value;
              setDraftText(text);
              onDraftChange?.(text);
            }}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            className={cn(draftText && 'pr-6', className)}
          />
          {draftText && !isSaving ? (
            // mousedown (not just click) is prevented -- a plain click handler would fire only
            // after the input has already blurred to this button, and that blur's own commit()
            // (use-editable-cell.ts) would run first and write the un-cleared draft. Preventing
            // the mousedown's default keeps focus on the input, so handleClear is the only commit
            // that happens.
            //
            // tabIndex={-1}: this is a pointer affordance, not a Tab stop -- find-adjacent-
            // focusable.ts's own selector already excludes tabindex="-1", which is what keeps this
            // button from becoming Tab's "next focusable element" ahead of the actual next cell
            // (Escape, or clearing the text and pressing Enter, cover the keyboard-only path).
            <button
              type="button"
              tabIndex={-1}
              aria-label={`Clear ${ariaLabel}`}
              className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleClear}
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>
        {error ? (
          <span id={errorId} role="alert" className="sr-only">
            {error}
          </span>
        ) : null}
      </>
    );
  }

  const { text, muted } = resolveEditableCellDisplay(state, format(value), emptyText);

  const button = (
    <DimeButton
      ref={displayRef}
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        'w-full justify-start px-2 font-normal',
        muted && 'text-muted-foreground',
        className,
      )}
      aria-label={composedAriaLabel}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? errorId : undefined}
      disabled={disabled || isSaving}
      disabledTooltip={disabled ? disabledTooltip : undefined}
      disabledTooltipFullWidth
      onClick={enterEditMode}
      onFocus={handleDisplayFocus}
    >
      {locked ? <Lock aria-hidden="true" className="mr-1 inline size-3 shrink-0" /> : null}
      <span className="truncate">{text}</span>
    </DimeButton>
  );

  return (
    <>
      {/* Skipped while disabled: DimeButton already wraps itself in a disabled-reason tooltip
          via disabledTooltip, and nesting a second Radix tooltip trigger on the same node would
          fire both on hover for no benefit -- the disabled reason is the more useful of the two. */}
      {state === 'inherited' && inheritedFrom && !disabled ? (
        <DimeTooltip content={inheritedFrom}>{button}</DimeTooltip>
      ) : (
        button
      )}
      {error ? (
        <span id={errorId} role="alert" className="sr-only">
          {error}
        </span>
      ) : null}
    </>
  );
}
